import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import { Calendar, Clock, ArrowRight, ArrowLeft } from "lucide-react";

export function BlogsListPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blogs").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blogs-page" className="pb-24">
      <section className="border-b border-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="FIELD NOTES" title={t("blogs.title")} description={t("blogs.subtitle")} testIdPrefix="blogs" />
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {loading ? <div className="text-slate-400">Loading…</div> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-slate-800 grid-border">
              {items.map((b) => (
                <Link
                  to={`/blogs/${b.slug}`}
                  key={b.id}
                  data-testid={`blog-card-${b.id}`}
                  className="group block bg-slate-950 hover:bg-slate-900 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-900">
                    <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 font-mono-spec text-[10px] text-slate-400">
                      <span className="inline-flex items-center gap-1"><Calendar size={11} /> {b.published_at}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {b.read_time}</span>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-white text-lg leading-tight group-hover:text-amber-400 transition-colors">{b.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 line-clamp-3">{b.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-amber-400 font-mono-spec text-xs">
                      {t("blogs.read_more")} <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/blogs/${slug}`).then(({ data }) => { setBlog(data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-5 py-24 text-slate-400">Loading…</div>;
  if (!blog) return <div className="max-w-3xl mx-auto px-5 py-24 text-slate-400">Article not found.</div>;

  return (
    <div data-testid="blog-detail-page" className="pb-24">
      <div className="relative aspect-[3/1] overflow-hidden border-b border-slate-800">
        <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      </div>
      <article className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
        <Link to="/blogs" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 font-mono-spec text-xs mb-6" data-testid="blog-back-link">
          <ArrowLeft size={12} /> ALL INSIGHTS
        </Link>
        <div className="flex flex-wrap gap-3 font-mono-spec text-[10px] text-slate-400">
          <span>{blog.published_at}</span>
          <span>·</span>
          <span>{blog.read_time}</span>
          <span>·</span>
          <span>{blog.author}</span>
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">{blog.title}</h1>
        <p className="mt-5 text-slate-300 text-lg leading-relaxed">{blog.excerpt}</p>
        <div className="mt-8 text-slate-300 leading-relaxed whitespace-pre-line">{blog.content}</div>
        <div className="mt-10 flex flex-wrap gap-2">
          {(blog.tags || []).map((t) => (
            <span key={t} className="px-3 py-1 border border-slate-800 text-slate-300 font-mono-spec text-[10px]">{t}</span>
          ))}
        </div>
      </article>
    </div>
  );
}
